import { BottomSheet } from "@/design-system/BottomSheet/BottomSheet";
import { Button } from "@/design-system/Button/Button";
import { Popover } from "@/design-system/Popover/Popover";
import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { SlidersHorizontal } from "lucide-react";
import { ReactNode, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

/**
 * A settings button and the surface it opens: an anchored Popover on desktop,
 * and on touch devices the same full-screen sheet Compare Players uses. The
 * caller's `panel` fills it and draws its own header and actions. A dot on the
 * button says something in there is set away from its default.
 */
export const CustomizeButton = ({
  label,
  panel,
  active = false,
  compact = false,
  bare = false,
  stretch = false,
  placement = "bottom",
  width,
  disabled = false,
}: {
  label: string;
  /** The settings; `compact` on touch devices, `close` shuts the surface. */
  panel: (props: { compact: boolean; close: () => void }) => ReactNode;
  /** Shows the dot. */
  active?: boolean;
  /** Icon only. */
  compact?: boolean;
  /** Icon only, with no border or fill. */
  bare?: boolean;
  stretch?: boolean;
  /** Desktop: below the button (a toolbar) or beside it (a rail). */
  placement?: "bottom" | "right";
  /** Desktop popover width. */
  width: number;
  /** Greyed and inert, e.g. while a solve runs: its result would ignore a change made now. */
  disabled?: boolean;
}) => {
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const [open, setOpen] = useState(false);
  // The full-screen sheet owns scrolling while it is up, as Compare's does.
  useScrollLock(isTouch && open);

  const trigger = (onClick?: () => void) => (
    <span className={cn("relative", stretch ? "flex w-full" : "inline-flex")}>
      <Button
        variant={bare ? "subtle" : "tertiary"}
        size="small"
        stretch={stretch}
        icon={<SlidersHorizontal />}
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
      >
        {compact || bare ? undefined : <span>{label}</span>}
      </Button>
      {active && !disabled && (
        <span
          aria-hidden
          className="pointer-events-none absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-surface bg-primary-blue"
        />
      )}
    </span>
  );

  // No surface behind a disabled trigger: the Popover listens on a wrapper,
  // which a click on a disabled button can still reach in some browsers.
  if (disabled) return trigger();

  if (isTouch) {
    return (
      <>
        {trigger(() => setOpen(true))}
        <BottomSheet
          isOpen={open}
          onClose={() => setOpen(false)}
          className="h-dvh max-w-full rounded-none bg-neutral-900"
        >
          {/* As Compare Players: the sheet's own wrapper is the one scroller,
              header and body flow inside it. */}
          <div className="flex min-h-full w-full flex-col">
            {panel({ compact: true, close: () => setOpen(false) })}
          </div>
        </BottomSheet>
      </>
    );
  }

  return (
    <Popover
      trigger={trigger()}
      placement={placement}
      align={placement === "bottom" ? "end" : "start"}
      width={width}
      negLabel=""
      posLabel=""
      newDesignSystem
    >
      {/* The Popover pads its body; the panel draws its own sections edge to
          edge, so the padding is given back here. */}
      {(close) => (
        <div className="-m-4">{panel({ compact: false, close })}</div>
      )}
    </Popover>
  );
};
